import React, { useEffect, useMemo, useRef } from 'react';
import { usePathname } from 'next/navigation';
import posthog from 'posthog-js';

import { Article, BRAND_TYPE } from '@repo/types';

import { YtVideosArrayData } from '@/hooks/api/yt/model';

import { ArticleCard } from './article-card';
import { SignInButton } from './sign-in-button';
import { YtVideoCard } from './yt-video-card';

const fakeArticle: Article = {
  id: 44887,
  title:
    'BBC sacks MasterChef UK host John Torode over racist language claim in Gregg Wallace report',
  url: 'https://www.abc.net.au/news/2025-07-16/masterchef-john-torode-racist-language-claim-gregg-wallace/105536574',
  source: 'abc.net.au',
  publishedAt: '2025-07-16T02:56:16.000Z',
  summary:
    "The Australian-born celebrity chef has denied the allegation, which the BBC says was substantiated in an investigation into his co-host Greg Wallace's behaviour on set.",
  content: '',
  updatedAt: '',
  brand: {
    id: 20,
    brand: 'ABC News (Australia)',
    logoCircleUrl:
      'https://assets.dubi.ai/brand-logos-circular/circle-18-abcnews_uk.png',
    logoLargeUrl:
      'https://assets.dubi.ai/brand-logos-flat-large/flat-large-18-abc_australia.png',
    logoSmallUrl:
      'https://assets.dubi.ai/brand-logos-flat-large/flat-large-18-abc_australia.png',
    pictureUrl: null,
    categories: [],
    description: '',
    isActive: true,
    createdAt: '',
    labels: [],
    sanitizeUrl: false,
    updatedAt: '',
    type: BRAND_TYPE.NEWS,
  },
  metrics: {
    id: 41238,
    credibilityScore: 85,
    credibilityFlag: 'HIGH',
    credibilityReasoning:
      'The article relies on official statements from the BBC and Banijay UK, along with direct quotes from involved parties. The reporting is factual and attributed to credible sources. However, the article presents allegations and denials, requiring readers to form their own conclusions.',
    biasScore: 30,
    biasLabel: 'Accountability Focused',
    biasLabelReasoning:
      "The article focuses on the BBC's handling of misconduct allegations and emphasizes the broadcaster's response to the situation. While presenting both sides, the article highlights the BBC's actions and statements, suggesting a focus on accountability and transparency.",
    claimedFactsScores: 65,
    opinionStatementsScores: 20,
    dubiousClaimsScores: 15,
    originalityScore: 70,
    originalityReasoning:
      "The article provides a clear overview of a new feature, but it primarily reports on OpenAI's announcements rather than offering in-depth analysis or original research.",
    disclaimer:
      'This article presents allegations and denials. Readers should consider all perspectives and verify information from multiple sources.',
    aiSummary:
      'The BBC fired John Torode after an investigation found he used racist language. This follows the severing of ties with Gregg Wallace over sexual misconduct allegations. The BBC acknowledged missed opportunities to address inappropriate behavior.',
  },
  createdAt: '2025-07-16T03:02:50.946Z',
  categories: [
    {
      id: 37,
      name: 'Entertainment',
      level: 1,
      createdAt: '',
      updatedAt: '',
    },
    {
      id: 50,
      name: 'Crime',
      level: 1,
      createdAt: '',
      updatedAt: '',
    },
    {
      id: 52,
      name: 'Media',
      level: 1,
      createdAt: '',
      updatedAt: '',
    },
  ],
  labels: [
    {
      id: 1,
      name: 'Controversial',
    },
    {
      id: 226,
      name: 'Opinion',
    },
    {
      id: 314,
      name: 'Expert',
    },
  ],
};

const fakeVideo: YtVideosArrayData = {
  id: 'cmi97508o0005na3bramvn6ca',
  youtubeId: '3Q6OfL5t5Cc',
  title:
    "Lee Zeldin: Jasmine Crockett's Epstein Claim, Trump Meets Mamdani + Cloud Seeding Is REAL! | PBD 687",
  description:
    "Patrick Bet-David sits down with Lee Zeldin to break down Jasmine Crockett’s explosive Epstein claims, Trump’s Oval Office meeting with Zohran \"Kawame\" Mamdani, and whether cloud seeding is real or a conspiracy theory.\n------\n📕 REGISTER FOR BPW 2025 - FRIDAY, DECEMBER 12TH 2025: https://bit.ly/3IU2YWx\n\n🎙️ FOLLOW THE PODCAST ON SPOTIFY: ⁠⁠https://bit.ly/4g57zR2\n\n🎙️ FOLLOW THE PODCAST ON ITUNES: ⁠⁠https://bit.ly/4g1bXAh\n\n🎙️ FOLLOW THE PODCAST ON ALL PLATFORMS: https://bit.ly/4eXQl6A\n\nⓂ️ CONNECT ON MINNECT: ⁠⁠https://bit.ly/4kSVkso \n\nⓂ️ PBD PODCAST CIRCLES: https://bit.ly/4mAWQAP\n\n🥃 BOARDROOM CIGAR LOUNGE: https://bit.ly/4pzLEXj\n\n🍋 ZEST IT FORWARD: https://bit.ly/4kJ71lc \n\n📕 PBD'S BOOK \"THE ACADEMY\": https://bit.ly/41rtEV4\n\n👔 BET-DAVID CONSULTING: https://bit.ly/4lzQph2 \n\n📺 JOIN THE CHANNEL: ⁠⁠⁠https://bit.ly/4g5C6Or\n\n💬 TEXT US: Text “PODCAST” to 310-340-1132 to get the latest updates in real-time! \n\nTIME STAMPS:\n00:00 - Show intro\n00:58 - Jasmine Crockett's Epstein claims. \n10:03 - Mamdani's trip to the White House\n27:39 - Hochul vs Stefanik for NY governor. \n36:23 - Affordability crisis in the U.S.\n51:38 - EPA's work to deregulate industries. \n58:44 - EPA's role in the California wildfires.\n1:10:05 - Are cloud-seeding & chemtrails real?\n1:29:00 - Nuclear energy in the U.S.\n1:37:15 - Negotiating with President Trump. \n\nSUBSCRIBE TO:\n@VALUETAINMENT   \n@ValuetainmentComedy \n@theunusualsuspectspodcast \n@HerTakePod \n@bizdocpodcast \n\nABOUT US:\nPatrick Bet-David is the founder and CEO of Valuetainment Media. He is the author of the #1 Wall Street Journal Bestseller “Your Next Five Moves” (Simon & Schuster) and a father of 2 boys and 2 girls. He currently resides in Ft. Lauderdale, Florida.",
  slug: 'lee-zeldin-jasmine-crockett-s-epstein-claim-trump-meets-mamdani-cloud-seeding-is-real-pbd-687-ec9843bb',
  duration: 6376,
  viewCount: 242126,
  url: 'https://youtu.be/3Q6OfL5t5Cc',
  uploadDate: '2025-11-21T18:31:18.648Z',
  createdAt: '2025-11-21T18:31:18.648Z',
  updatedAt: '2025-11-21T20:17:13.747Z',
  channel: {
    id: 'cmhy0pdv40005x24fpr117e4j',
    youtubeId: 'UCGX7nGXpz-CmO_Arg-cgJ7A',
    name: 'PBD Podcast',
    channelUrl: 'https://www.youtube.com/@PBDPodcast/videos',
    channelHandle: 'PBDPodcast',
    chanelProfilePictureUrl:
      'https://yt3.googleusercontent.com/d2IID2loX1_ET_6U5qMAap97jQfBjAz9qXXRRDBclOsll4Fqm88MQQFNoUEas8kSdwLZlk32=s120-c-k-c0x00ffffff-no-rj',
  },
  thumbnailUrl: 'https://i.ytimg.com/vi/3Q6OfL5t5Cc/hqdefault.jpg',
  sharingUrl: null,
  videoAnalysis: {
    keyPointsByType: {
      short: {
        count: 9,
        totalDurationSeconds: 5864,
        saveTime: {
          seconds: 512,
          formatted: { number: 9, type: 'm' },
          label: '9 min',
        },
        keyPoints: [],
      },
    },
    categories: [],
    impactLevels: [],
    metrics: {
      credibilityScore: 65,
    },
  },
};

interface LoginWallCardProps {
  type: 'article' | 'video';
}

export const LoginWallCard: React.FC<LoginWallCardProps> = ({ type }) => {
  const pathname = usePathname();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const viewedFiredRef = useRef(false);
  const surface = `wall_${type}_iframe`;

  // `login_wall_clicked` is now fired synchronously inside
  // useAuth.handleSignIn (triggered by SignInButton via wallType prop).
  // No wrapper-level onClickCapture — that pattern was bypass-prone for
  // any portal-rendered button and dropped events silently.

  // Fire `login_wall_viewed` once per mount, gated on the wall actually
  // entering the viewport (≥30% visible). The wall already only renders
  // for unauthenticated users, so the fire condition is "the wall was
  // visible to a non-signed-in extension user."
  useEffect(() => {
    if (viewedFiredRef.current) return;
    const node = wrapperRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !viewedFiredRef.current) {
            viewedFiredRef.current = true;
            posthog.capture('login_wall_viewed', {
              surface,
              type,
              pathname,
            });
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [type, pathname, surface]);

  const CardComponent = useMemo(() => {
    if (type === 'article') {
      return (
        <ArticleCard
          article={fakeArticle}
          className="relative blur-sm min-h-[206px] cred-md:min-h-[241px] cred-lg:min-h-[259px] cred-xl:min-h-[343px]"
        />
      );
    }
    return (
      <YtVideoCard
        ytData={fakeVideo}
        className="relative blur-sm min-h-[206px] cred-md:min-h-[241px] cred-lg:min-h-[259px] cred-xl:min-h-[343px] md:min-h-0 shadow-[0px_0px_4px_0px_rgba(0,0,0,0.25)]"
      />
    );
  }, [type]);

  return (
    <div ref={wrapperRef} className="relative size-full pt-2 pb-6">
      {CardComponent}
      <div className="absolute inset-x-4 inset-y-10 flex items-center justify-center md:inset-x-[25px]">
        <div className="flex size-full max-h-[200px] flex-col items-center justify-center gap-3 rounded-md border border-[#C0C0C0] bg-white px-4 py-2.5 text-black">
          <div className="flex flex-col items-center justify-center gap-1 text-center">
            <span className="text-base cred-lg:text-[17px] font-bold cred-xl:text-lg">
              Sign in to continue {type === 'article' ? 'reading' : 'watching'}
            </span>
            <span className="text-xs cred-lg:text-[13px] md:text-sm cred-xl:text-base">
              Get access to more {type === 'article' ? 'article' : 'video'}{' '}
              insights
            </span>
          </div>
          <SignInButton
            onlySignIn
            surface={surface}
            wallType={type}
            className="w-fit"
          />
        </div>
      </div>
    </div>
  );
};
